import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type {
  PublicProfileAnalyticsActorKind,
  PublicProfileAnalyticsRange,
  PublicProfileAnalyticsRecentActivity,
} from '@tabbio/types';
import { useAuth } from '../../hooks/useAuth';
import { usePublicProfileAnalytics } from '../../hooks/useAnalyticsApi';
import { useTailoredCVs } from '../../hooks/useTailoredCvApi';
import { useEntitlement } from '../../providers/SubscriptionProvider.shared';
import { useUpgradeModal } from '../../providers/UpgradeModalProvider';
import { buildPublicCvDisplayLink } from '../../lib/public-web';
import {
  activityMetric,
  buildCvAnalyticsChart,
  buildCvAnalyticsTrend,
  chartAxisTimes,
  chartBarEnd,
  chartBarStart,
  cvOptionLabel,
  CV_ANALYTICS_METRICS,
  activityAge,
  formatCvAnalyticsCount,
  MAIN_CV_ANALYTICS_SCOPE,
  formatChartDate,
  isCvAnalyticsPremium,
  metricTotal,
  selectCvAnalyticsActivity,
  type CvAnalyticsChart,
  type CvAnalyticsCvOption,
  type CvAnalyticsMetric,
  type CvAnalyticsTrend,
} from '../../lib/cv-analytics-view';

/** One shared state object: CV, duration and metric move together. */
export interface CvAnalyticsScope {
  range: PublicProfileAnalyticsRange;
  cvId: string | null;
  metric: CvAnalyticsMetric;
}

export const DEFAULT_CV_ANALYTICS_SCOPE: CvAnalyticsScope = {
  range: 'all',
  cvId: null,
  metric: 'views',
};

export interface CvAnalyticsMetricView {
  key: CvAnalyticsMetric;
  label: string;
  value: string;
  trend: CvAnalyticsTrend | null;
  trendLabel: string | null;
  trendAccessibilityLabel: string | null;
}

export interface CvAnalyticsActivityView {
  id: string;
  metric: CvAnalyticsMetric;
  title: string;
  action: string;
  /** The CV the event belongs to, shown (and tappable) under "All" only. */
  cv: CvAnalyticsCvOption | null;
  /** Where the event happened when it was not a CV (profile, share link, Tabbio). */
  placeLabel: string | null;
  age: string;
  /** Who it was: a named member or company, else anonymous. */
  actor: {
    kind: PublicProfileAnalyticsActorKind;
    id: string | null;
    name: string;
    imageUrl: string | null;
  };
}

export function useCvAnalyticsScopeState(initial?: Partial<CvAnalyticsScope>) {
  const [scope, setScope] = useState<CvAnalyticsScope>({
    ...DEFAULT_CV_ANALYTICS_SCOPE,
    ...initial,
  });
  const update = (patch: Partial<CvAnalyticsScope>) =>
    setScope((current) => ({ ...current, ...patch }));
  return { scope, setScope, update };
}

/**
 * Everything the quick look and the full screen draw, for one scope. The
 * server decides what the viewer may see (`detail`); the entitlement only
 * fills in while the first answer loads.
 */
export function useCvAnalytics(
  scope: CvAnalyticsScope,
  { enabled = true, entryPoint }: { enabled?: boolean; entryPoint: string },
) {
  const { t, i18n } = useTranslation('seeker');
  const locale = i18n.language;
  const { user } = useAuth();
  const entitled = useEntitlement('individual.basic_analytics').allowed;
  const upgradeModal = useUpgradeModal();
  const query = usePublicProfileAnalytics(scope.range, enabled, scope.cvId);
  const cvsQuery = useTailoredCVs(enabled && Boolean(user));
  const data = query.data;
  const premium = isCvAnalyticsPremium(data, entitled);
  // A plan change (an upgrade from this very screen) makes every cached
  // answer the wrong tier: fetch again rather than keep showing Free.
  const queryClient = useQueryClient();
  const lastEntitled = useRef(entitled);
  useEffect(() => {
    if (lastEntitled.current === entitled) return;
    lastEntitled.current = entitled;
    void queryClient.invalidateQueries({ queryKey: ['public-profile', 'analytics'] });
    void queryClient.invalidateQueries({ queryKey: ['public-profile', 'analytics-actor'] });
  }, [entitled, queryClient]);
  const fmt = (value: number) => formatCvAnalyticsCount(value, locale);

  // The Main CV is the profile-built public CV, not a row in the CV list.
  const mainCvTitle = t('cvAnalytics.filter.mainCv');
  const cvOptions: CvAnalyticsCvOption[] = useMemo(
    () => [
      { id: MAIN_CV_ANALYTICS_SCOPE, title: mainCvTitle },
      ...(cvsQuery.data ?? [])
        .filter((cv) => cv.id && cv.title?.trim())
        .map((cv) => ({ id: cv.id, title: cv.title.trim() })),
    ],
    [cvsQuery.data, mainCvTitle],
  );

  const username = (user?.username ?? '').trim();
  const host = username ? buildPublicCvDisplayLink(username)?.label ?? null : null;

  const metricLabel = (metric: CvAnalyticsMetric) => t(`cvAnalytics.metric.${metric}`);
  const trendLabel = (trend: CvAnalyticsTrend | null) => {
    if (!trend) return null;
    if (trend.kind === 'new') return t('cvAnalytics.trend.newLabel');
    const arrow = trend.direction === 'up' ? '↗ ' : trend.direction === 'down' ? '↘ ' : '';
    return `${arrow}${fmt(trend.percent)}%`;
  };
  // Says which windows were compared: the selected range against the one
  // before it, or for All the last 30 days against the 30 before.
  const windowDays = data?.comparison?.windowDays ?? 30;
  const trendAccessibilityLabel = (trend: CvAnalyticsTrend | null) => {
    if (!trend) return null;
    if (trend.kind === 'new') return t('cvAnalytics.trend.new', { count: windowDays });
    return t(`cvAnalytics.trend.${trend.direction}`, {
      count: windowDays,
      percent: fmt(trend.percent),
    });
  };

  const metrics: CvAnalyticsMetricView[] = CV_ANALYTICS_METRICS.map((key) => {
    const total = metricTotal(data?.totals, key);
    const trend = data ? buildCvAnalyticsTrend(data.comparison, key) : null;
    return {
      key,
      label: metricLabel(key),
      value: fmt(total),
      trend,
      trendLabel: trendLabel(trend),
      trendAccessibilityLabel: trendAccessibilityLabel(trend),
    };
  });
  // Every view, download and share in scope: what the feed could list.
  const totalEvents = CV_ANALYTICS_METRICS.reduce(
    (sum, key) => sum + metricTotal(data?.totals, key),
    0,
  );
  // Totals or a feed entry: employer views inside Tabbio list without
  // counting toward the totals.
  const hasAnyActivity =
    totalEvents > 0 || Boolean(data?.breakdown.recentActivity.length);

  const chart: CvAnalyticsChart = buildCvAnalyticsChart(
    data?.breakdown.series,
    scope.metric,
    scope.range,
  );
  const axis = chartAxisTimes(chart).map((time) => formatChartDate(time, locale));
  const barLabel = (index: number) => {
    const start = formatChartDate(chartBarStart(chart, index), locale);
    const label =
      chart.daysPerBar > 1
        ? `${start} – ${formatChartDate(chartBarEnd(chart, index), locale)}`
        : start;
    return `${label}: ${fmt(chart.bars[index] ?? 0)}`;
  };

  const placeLabel = (surface: string) =>
    surface === 'share_link'
      ? t('cvAnalytics.place.shareLink')
      : surface === 'profile'
        ? t('cvAnalytics.place.profile')
        : null;

  const ageLabel = (createdAt: string) => {
    const age = activityAge(createdAt);
    return age ? t(`cvAnalytics.age.${age.unit}`, { count: age.count }) : '';
  };

  // One mapping for the feed and the actor detail sheet, so both read alike.
  const toActivityView = (item: PublicProfileAnalyticsRecentActivity): CvAnalyticsActivityView => {
    const metric = activityMetric(item.eventType) ?? 'views';
    // The profile and private share links are not a CV; a CV view with no
    // id (or the `master` placeholder) is the Main CV.
    const place = item.inTabbio
      ? t('cvAnalytics.place.tabbio')
      : item.cvId
        ? null
        : placeLabel(item.surface);
    const actor = item.actor?.kind && item.actor.kind !== 'anonymous' && item.actor.name
      ? item.actor
      : null;
    const cvKey =
      !item.cvId || item.cvId === 'master' ? MAIN_CV_ANALYTICS_SCOPE : item.cvId;
    const cvTitle = place ? null : cvOptionLabel(cvOptions, cvKey);
    return {
      id: item.id,
      metric,
      title: actor?.name ?? t('cvAnalytics.hub.anonymousVisitor'),
      action: t(`cvAnalytics.activityAction.${metric}`),
      cv: cvTitle ? { id: cvKey, title: cvTitle } : null,
      placeLabel: place,
      age: ageLabel(item.createdAt),
      actor: actor
        ? { kind: actor.kind, id: actor.id, name: actor.name!, imageUrl: actor.imageUrl }
        : { kind: 'anonymous', id: null, name: t('cvAnalytics.hub.anonymousVisitor'), imageUrl: null },
    };
  };

  const activity: CvAnalyticsActivityView[] = selectCvAnalyticsActivity(
    data?.breakdown.recentActivity,
  ).map(toActivityView);

  const selectedCvLabel = cvOptionLabel(cvOptions, scope.cvId);
  const periodLabel =
    scope.range === 'all'
      ? t('cvAnalytics.range.allTime')
      : t('cvAnalytics.range.lastDays', { count: Number.parseInt(scope.range, 10) });
  // Only the non-default filters show on the trigger.
  const filterLabel = scope.cvId
    ? scope.range === 'all'
      ? selectedCvLabel ?? t('cvAnalytics.filter.cv')
      : `${selectedCvLabel ?? t('cvAnalytics.filter.cv')} · ${periodLabel}`
    : scope.range === 'all'
      ? t('cvAnalytics.filter.all')
      : periodLabel;

  const openUpgrade = (entry: string = entryPoint) =>
    upgradeModal.open({
      placement: 'analytics',
      entryPoint: entry,
      planSet: 'individual',
      recommendedTier: 'plus',
      featureKey: 'individual.basic_analytics',
      reason: 'wrong_tier',
      returnTo: '/analytics',
    });

  const liveSummary = t('cvAnalytics.liveSummary', {
    scope: selectedCvLabel ?? t('cvAnalytics.filter.all'),
    period: periodLabel,
    views: metrics[0]!.value,
    downloads: metrics[1]!.value,
    shares: metrics[2]!.value,
  });

  return {
    query,
    data,
    premium,
    isLoading: query.isLoading && !data,
    error: !data ? query.error : null,
    isUpdating: query.isPlaceholderData || (query.isFetching && !query.isLoading),
    host,
    cvOptions,
    metrics,
    totalEvents,
    hasAnyActivity,
    chart,
    axis,
    barLabel,
    activity,
    toActivityView,
    fmt,
    filterLabel,
    periodLabel,
    selectedCvLabel,
    liveSummary,
    openUpgrade,
  };
}

export type CvAnalyticsModel = ReturnType<typeof useCvAnalytics>;
